/* eslint-disable react/no-danger */
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { lessonJsonLD, pageTitle } from 'utils/common.util';
import { getChapterAction } from 'store/course/course.action';
import request, { siteURL } from 'services/request';
import { CourseUrl } from 'services/routes';
import { CourseType } from 'types/course.type';

const Navbar = dynamic(() => import('components/Navbar'));
const Lesson = dynamic(() => import('components/Lesson'));
const Head = dynamic(() => import('next/head'));

type PageType = { course?: CourseType };
const LessonPage: React.FC<PageType> = ({ course }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const token = Cookies.get('token');
  const user = useSelector((state) => state.account.user);
  const { courseId, lessonId, lessonName }: any = router.query; // eslint-disable-line
  const lesson = course?.chapters
    ?.map((item) => item.lessons.map((k) => k))
    .flat()
    .find((item) => item.id === Number(lessonId));

  const url = `${siteURL}/workshop/${courseId}/lesson/${lessonId}/${lessonName}/`;
  const courseUrl = `${siteURL}/workshop/${courseId}/${course?.title}`;
  const ld = lessonJsonLD(course, url, courseUrl, lessonName, lesson);

  useEffect(() => {
    courseId && !course && dispatch(getChapterAction(courseId));
  }, [courseId]);
  useEffect(() => {
    token && !!course && !course?.registered && dispatch(getChapterAction(courseId)); // when user login
  }, [user]);

  return (
    <>
      <Head>
        <title>{pageTitle(`${course?.title} - ${lessonName}`)}</title>
        <meta name="description" content={course.description} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1 user-scalable=no"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />

        {/* Facebook Meta Tags  */}
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${course?.title} - ${lessonName}`} />
        <meta property="og:description" content={course.description} />
        <meta property="og:image" content={course.image} />
        <meta property="og:image:width" content="1920" />
        <meta property="og:image:height" content="1080" />

        {/* Twitter Meta Tags  */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={siteURL} />
        <meta property="twitter:url" content={url} />
        <meta name="twitter:title" content={`${course?.title} - ${lessonName}`} />
        <meta name="twitter:description" content={course.description} />
        <meta name="twitter:image" content={course.image} />
        <meta name="twitter:creator" content="@taalei-edu" />
        <meta name="twitter:site" content="@taalei-edu" />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ld }} />
      </Head>

      <Navbar />
      <Lesson lesson={lesson} course={course} />
    </>
  );
};

export default LessonPage;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getServerSideProps = async ({ req, res, params }) => {
  const { token } = req.cookies;
  const response: any = await request.get(CourseUrl(params.courseId), { token }); // eslint-disable-line
  const isRegistered = response.data?.registered;
  res.setHeader(
    'Cache-Control',
    `public, s-maxage=${isRegistered ? '360' : '1'}, stale-while-revalidate=${
      isRegistered ? '3600' : '1'
    }`,
  );

  if (!response.ok) {
    return response.status > 499
      ? { redirect: { destination: '/500' } }
      : { notFound: true };
  }

  return { props: { course: response.data } };
};
